<template lang="pug">
//- large layout (clickable tabs)
.screen-tabs.surface.bg-paper.corners.overflow-hidden.w-full.fade-in(
  ref="elRef"
  v-if="audioDoc || wordDoc || useLargeLayout"
)
  uc-preload-sfx(name="tabs")
  tab-group(:onChange="open")
    tab-list.w-full.flex.bg-gray-200(:class="'dark_bg-gray-900/40'")
      tab-button(
        :key="tab.label"
        as="template"
        v-for="(tab, i) in screen.tabs"
        v-slot
      )
        button.ripple.group.flex-1.flex.flex-col.focus-visible_ring-4.items-center.justify-center.text-center(
          :class="[twMerge('px-4 py-2.5 text-sm leading-5 font-medium text-primary-500 transition-all focus_outline-none outline-none ripple_bg-gray-500/10 border-b-4 border-b-transparent bg-gray-50 dark_bg-gray-900/40 z-1 hover_bg-gray-50/80 dark_hover_bg-gray-900/80', i === currentIndex ? ' z-2 border-b-secondary-500' : 'hover:bg-white/[0.12]', screen.styles?.['tab-button'], tab.styles?.['tab-button'], i === currentIndex ? screen.styles?.['tab-button-line'] : null, i === currentIndex ? tab.styles?.['tab-button-line'] : null), { 'ripple_bg-transparent': i !== currentIndex }]"
          uc-word-skip=""
          uc-dont-read=""
          data-sfx="tabs"
          :data-tab-id="`${screen.id}-tab-${i}`"
        )
          div
            div(:class="twMerge('w-20 h-20 mx-auto mb-2 flex items-center', screen.styles?.['tab-label-image'], tab.styles?.['tab-label-image'])" v-if="tab['img-label']")
              uc-asset.flex-1.h-full(:src="tab['img-label']")
            uc-editable(
              :class="twMerge('text-lg font-semibold text-gray-900 text-center dark_text-white', screen.styles?.['tab-label-text'], tab.styles?.['tab-label-text'])"
              :editPath="`${editPath}.tabs.${i}.label`"
              :k="`${tab.id}.label`"
              :text="tab.label"
              v-if="tab.label"
            )

    tab-panels
      .collapsed-items
        .auto-alpha-0.auto-alpha-fast.collapsed-item.p-8.md_px-12(
          :class="{ 'auto-alpha-1 auto-alpha-slow': i === currentIndex }"
          :key="tab.text"
          v-for="(tab, i) in screen.tabs"
        )
          audio-marker(:name="`${screen.id}-tab-${i}`")

          //- include the tab labels for the word doc
          //- and in the audio doc only if we don't have a tab heading/subheading/title (avoids possible repetition)
          uc-text.heading(
            :k="`${tab.id}.label`"
            :text="tab.label"
            type="h3"
            v-if="(wordDoc && !audioDoc) || (audioDoc && !tab.heading && !tab.subheading && !tab.title)"
          )

          uc-content(
            :editPath="`${editPath}.tabs.${i}`"
            :screen="{ h: 'h4', ...tab }"
          )

//- mobile layout
tab-group.screen-tabs.mobile-layout(ref="elRef" v-else="")
  tab-list.w-full.flex.flex-col.gap-10.fade-in
    .surface.bg-paper.corners.flex.flex-col.gap-10.p-8(
      :class="twMerge('bg-gray-200 dark_bg-gray-900/40', screen.styles?.['tab-button'], tab.styles?.['tab-button'])"
      v-for="(tab, i) in screen.tabs"
      )
      div(:class="twMerge('border-b-4 border-b-secondary-500', screen.styles?.['tab-button-line'], tab.styles?.['tab-button-line'])")
        div(:class="twMerge('w-20 h-20 mx-auto mb-2 flex items-center', screen.styles?.['tab-label-image'], tab.styles?.['tab-label-image'])" v-if="tab['img-label']")
          uc-asset.flex-1.h-full.mb-5(:src="tab['img-label']")

        uc-editable.mb-5.mx-auto(
          :class="twMerge('text-lg font-semibold text-gray-900 text-center dark_text-white', screen.styles?.['tab-label-text'], tab.styles?.['tab-label-text'])"
          :editPath="`${editPath}.tabs.${i}.label`"
          :k="`${tab.id}.label`"
          :text="tab.label"
          v-if="tab.label"
        )

      audio-marker(:name="`${screen.id}-tab-${i}`")

      uc-content(
        :editPath="`${editPath}.tabs.${i}`"
        :screen="{ h: 'h4', ...tab }"
      )
</template>

<script setup lang="ts">
import {
  ref,
  onMounted,
  nextTick,
  inject,
  computed,
  watch,
  watchEffect,
  toRef,
  withDefaults,
  type Ref
} from 'vue';
import {
  setViewed,
  getViewed,
  setCompleted,
  getCompleted,
  set
} from '../lib/SCORM';
import useSuspend from '../lib/useSuspend';
import useTranslations from '../stores/useTranslations';
import {
  TabGroup,
  TabList,
  Tab as TabButton,
  TabPanels
} from '@headlessui/vue';
import { play, queue, stop } from '../lib/audio';
import twMerge from '../lib/mergeStyles';
import {
  breakpointsTailwind,
  useBreakpoints,
  useEventListener
} from '@vueuse/core';
import isQuizPage from '../lib/isQuizPage';
import useScreenAudio from '../lib/useScreenAudio';
import tabsSchema from '../lib/screen-schemas/screen-tabs.schema';

type ScreenTab = UcContent & {
  id?: string;
  label?: string;
  'tab-alt'?: string;
  audio?: string;
  speech?: string;
};

type ScreenTabs = {
  id: string;
  tabs: ScreenTab[];
};

const elRef = ref<HTMLElement | null>(null);
const props = withDefaults(
  defineProps<{
    editPath: string;
    screen: ScreenTabs;
    /** Ancestor owns section intro when nested under `uc-screen` / `templates[]`. */
    nested?: boolean;
  }>(),
  { nested: false }
);
const pageId = inject('pageId') as object;
const sectionId = inject('sectionId') as string;
const sectionActive = inject('sectionActive') as Ref<boolean>;
const { playSlot, queueSlot } = useScreenAudio(
  toRef(props, 'screen'),
  tabsSchema
);
const audioDoc = inject('audioDoc');
const wordDoc = inject('wordDoc');
const breakpoints = useBreakpoints(breakpointsTailwind);
const largeDevice = breakpoints.greater('md');
const extraLargeDevice = breakpoints.greater('lg');
const manyTabs = computed(() => props.screen.tabs.length > 6);
const useLargeLayout = computed(
  () => (largeDevice.value && !manyTabs.value) || extraLargeDevice.value
);
const suspend = useSuspend();
const { translations } = useTranslations();
let currentIndex = ref<number>();

// Returns a prop-level audio URL if explicitly set on the tab; S3/JiT handled by playSlot()
const getTabAudio = (tab: ScreenTabs['tabs'][0]) =>
  tab.audio || tab.speech || null;

// FIXME: tab labels can sometimes contain chars we may not want in the key name, e.g. quotes, apostrophies, spaces etc. Can we just use the tab id else index?
const getKey = (tab: ScreenTabs['tabs'][0], i: number) =>
  tab.id
    ? tab.id
    : tab.label
      ? encodeURIComponent(tab.label.trim()).replace(' ', '_').toLowerCase()
      : i;

const getTabAriaLabel = (i: number) => {
  const tab = props.screen.tabs[i];
  const targetText =
    tab['tab-alt'] || tab.heading || tab.title || tab.subheading || '';
  const fallbackId = `${tab.id}.tab-alt`;

  // TODO - add robust error messaging that does not interupt workflow
  //if (targetText === '') {
  //  bugAlert(`#${props.screen.id}\n\n A tab button is missing a label, heading or subheading. If the tab is meant to be unlabeled, please add a 'tab-alt' field for use with screen readers.`);
  //}

  return hasTranslation(targetText, fallbackId);
};

const open = (i: number, { ignoreAudio = false } = {}) => {
  const { screen } = props;
  const tab = screen.tabs[i];
  currentIndex.value = i;

  setViewed(`tab_${screen.id}_${getKey(tab, i)}`);

  const allViewed = screen.tabs.every(
    (_tab, _i) =>
      getViewed(`tab_${screen.id}_${getKey(_tab, _i)}`) ||
      getCompleted(`tab_${screen.id}_${getKey(_tab, _i)}`)
  );

  const audio = getTabAudio(tab);

  if (!ignoreAudio) {
    stop();
    // Tier 1: explicit prop-level tab audio URL
    if (audio) {
      play(audio);
    } else {
      // Tier 2/3: schema-based — resolveSlot handles S3 (getAudioURL) or JiT
      playSlot(`tab-${i}`); // fire and forget
    }
  }

  if (allViewed) {
    setCompleted(sectionId);
  }
};

onMounted(async () => {
  if (!currentIndex.value) {
    open(0, { ignoreAudio: true });
  }

  // The first section on a page never receives a false→true sectionActive
  // transition, so its intro must be played here (mirrors uc-screen.vue).
  await nextTick();
  if (!props.nested && sectionActive.value) void playSectionAudio();
});

const completeOnMobile = () => {
  if (
    sectionActive.value &&
    !useLargeLayout.value &&
    !getCompleted(sectionId)
  ) {
    setCompleted(sectionId);
  }
};

function hasTranslation(key, fallback) {
  // get the translation if it exists
  let maybeTranslated =
    translations?.[suspend.locale]?.[key] ||
    translations?.[suspend.locale]?.[fallback];

  return maybeTranslated || key;
}

function applyTabAriaLabel() {
  if (!elRef.value || !useLargeLayout.value) return;
  // for tabs without a label set an aria-label on the button. As we don't want aria-labels when the button has text only use this when tab.label is empty.
  try {
    props.screen.tabs.forEach((tab, i) => {
      if (!tab.label && elRef.value?.querySelector) {
        const target = elRef.value?.querySelector(
          `[data-tab-id="${props.screen.id}-tab-${i}"]`
        );
        const tabAriaLabel = getTabAriaLabel(i);
        if (target && tabAriaLabel)
          target.setAttribute('aria-label', getTabAriaLabel(i));
      }
    });
  } catch (e) {
    console.error('Error applying aria-labels to tabs', e);
  }
}

// watch for suspend.locale changes and update the aria-labels
watch(
  () => suspend.locale,
  () => applyTabAriaLabel
);

// when the screen becomes active check if we need to auto-complete for mobile layouts
watch(sectionActive, () => {
  if (sectionActive.value) completeOnMobile();
});

// Schema audio sequence: play intro, then queue the initial/active tab.
// Runs instead of the generic uc-screen.vue intro watcher (managesOwnSectionAudio: true).
// Sequential await ensures the queue call happens after play() clears the queue,
// avoiding a race condition where a fast-resolving tab-0 JiT response gets discarded.
const playSectionAudio = async () => {
  await playSlot('intro');

  const i = currentIndex.value ?? 0;
  const tab = props.screen.tabs[i];
  const propAudio = getTabAudio(tab);
  if (propAudio) {
    queue(propAudio, { owner: props.screen.id });
  } else {
    queueSlot(`tab-${i}`); // fire-and-forget: queues when resolved
  }
};

watch(sectionActive, active => {
  if (props.nested || !active) return;
  void playSectionAudio();
});

watchEffect(() => {
  if (useLargeLayout.value) {
    applyTabAriaLabel();
  }
});

useEventListener(window, 'resize', completeOnMobile);

const app = document.querySelector('#app');
app?.addEventListener('quizReset', async (e: Event) => {
  if (await isQuizPage(e as CustomEvent, pageId as any)) {
    props.screen.tabs.forEach((_tab, _i) => {
      set(`viewed.tab_${props.screen.id}_${getKey(_tab, _i)}`, false);
      set(`completed.tab_${props.screen.id}_${getKey(_tab, _i)}`, false);
    });
  }
  currentIndex.value = -1;

  open(0, { ignoreAudio: true });
});
</script>
